import { Injectable } from '@angular/core';

export interface EventsFilterState {
  status: string;
  season: string;
  level: string;
  startDate: string;
  endDate: string;
  search: string;
}

const FILTER_KEYS: (keyof EventsFilterState)[] = [
  'status',
  'season',
  'level',
  'startDate',
  'endDate',
  'search',
];

const EMPTY_FILTERS: EventsFilterState = {
  status: '',
  season: '',
  level: '',
  startDate: '',
  endDate: '',
  search: '',
};

const SESSION_KEY = 'events_list_filters';
const CONTEXT_KEY = 'events_list_filters_context';

@Injectable({
  providedIn: 'root',
})
export class EventsFilterStateService {
  /** Clear saved filters when the list changes (e.g. events -> broadcasts) */
  resetOnContextChange(context: string): void {
    if (sessionStorage === undefined) return;
    try {
      if (sessionStorage.getItem(CONTEXT_KEY) !== context) {
        sessionStorage.removeItem(SESSION_KEY);
        sessionStorage.setItem(CONTEXT_KEY, context);
      }
    } catch {
      /* ignore */
    }
  }

  /** Load filters from session storage */
  restoreFilters(): EventsFilterState {
    const loaded = this.loadFromSession();
    return loaded ?? EMPTY_FILTERS;
  }

  /** Save filters to session storage */
  persistFilters(filters: Partial<EventsFilterState>): void {
    const normalized = this.normalize(filters);
    this.saveToSession(normalized);
  }

  /** Drop any persisted filters — called when leaving the events list for another page, so a later visit starts fresh. */
  clearFilters(): void {
    if (sessionStorage === undefined) return;
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      /* ignore */
    }
  }

  private normalize(f: Partial<EventsFilterState>): EventsFilterState {
    const result = { ...EMPTY_FILTERS };
    FILTER_KEYS.forEach((k) => {
      result[k] = String(f[k] ?? '').trim();
    });
    return result;
  }

  private hasAnyValue(f: EventsFilterState): boolean {
    return FILTER_KEYS.some((k) => !!f[k]);
  }

  private loadFromSession(): EventsFilterState | null {
    if (sessionStorage === undefined) return null;
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Record<string, string>;
      return this.normalize(parsed);
    } catch {
      return null;
    }
  }

  private saveToSession(f: EventsFilterState): void {
    if (sessionStorage === undefined) return;
    try {
      if (this.hasAnyValue(f)) {
        const stored = Object.fromEntries(
          FILTER_KEYS.filter((k) => f[k]).map((k) => [k, f[k]])
        );
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(stored));
      } else {
        sessionStorage.removeItem(SESSION_KEY);
      }
    } catch {
      /* ignore */
    }
  }
}
